import {useCallback, useContext, useState} from "react";
import {  Button, Input, Table, Row, Col, Tag, Upload, message, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { ConfiguratorContext} from "../context";
import {Customer, DEFAULT_THROTTLE} from "../api/models";
import {throttle} from "lodash";
import {ColumnType, TablePaginationConfig} from "antd/lib/table";
import {FilterValue, SorterResult} from "antd/lib/table/interface";
import { useEditCustomerStep } from "../components/customer_entry";
import Utils from "../util/util";
import DealerMultipleSelector from "../components/DealerMultipleSelector";
import { UploadOutlined, DownloadOutlined, SyncOutlined } from "@ant-design/icons";
import ModalWizard from "../components/ModalWizard";
import useDealers from "../swr/useDealers";
import useCustomerList from "../swr/useCustomerList";
import CustomerTagSelector from "../components/customer_tag_selector";

enum UPLOADING_STATUS {
  INIT = "init",
  ERROR = "error",
  DONE = "done",
  UPLOADING = "uploading"
}

const CustomerList = () => {

  const configurator = useContext(ConfiguratorContext);
  const [filter, setFilter] = useState<string | undefined>();
  const [open, setOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer>();
  const [filterDealers, setFilterDealers] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [uploadStatus, setUploadStatus] = useState(UPLOADING_STATUS.INIT);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
  });
  const [sorter, setSorter] = useState<SorterResult<Customer>>({
    columnKey: "name",
    order: "ascend",
  });

  const dealers = useDealers();
  const customers = useCustomerList({
    search: filter,
    dealerIds: filterDealers,
    tags: filterTags,
    current: pagination.current,
    pageSize: pagination.pageSize,
    sorter,
  });

  const canViewTags = ( configurator.isAdmin() || configurator.isEngineering() || configurator.isSalesDesk() || configurator.isDealerManagement() ) && !configurator.isDealerSales();

  const updateFilter = (value: string | undefined) => {
    setFilter(value);
    setPagination( p => ({ ...p, current: 1 }) );
  };
  const debounceSetFilter = useCallback(throttle(updateFilter, DEFAULT_THROTTLE), []);

  const columns: ColumnType<Customer>[] = [

    {
      title: "Name",
      key: "name",
      sorter: true,
      defaultSortOrder: "ascend",
      render: (_, customer: Customer) =>
          <Button type="text" className={"ghostBmButton"} style={{textDecoration: "underline"}} onClick={() => {onEdit(customer)}}>{customer.name}</Button>,
    },
    {
      title: "Address",
      dataIndex: "address",
      render: (_, customer: Customer) => (
        <span>{Utils.getAddress(customer)}</span>
      ),
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
    },
    {
      title: "Phone",
      dataIndex: "contactPhone",
    },
    {
      title: "E-mail",
      dataIndex: "contactEmail",
    },
    {
      title: "Dealer",
      dataIndex: "dealer",
      render: (_, customer: Customer) => (
        <span>{dealers.data?.content.find(d => d.id === customer.dealerId)?.name}</span>
      ),
    },
    ...( canViewTags ? [{
      title: "Tags",
      dataIndex: "tags",
      render: (_, customer: Customer) => <>{customer.tags?.map( t => <Tag key={t}>{t}</Tag> )}</>,
    } as ColumnType<Customer>] : [] ),
  ];

  const handleTableChange = (pagination: TablePaginationConfig, _filters: Record<string, FilterValue | null>, sorter: SorterResult<Customer> | SorterResult<Customer>[]) => {
    setPagination(pagination);
    setSorter([sorter].flat()[0]);
  };

  const onFilterDealers = (value: string[] | undefined) => {
    setFilterDealers(value || []);
    setPagination( p => ({ ...p, current: 1 }) );
  };

  const onFilterTags = (value: string[] | undefined) => {
    setFilterTags(value || []);
    setPagination( p => ({ ...p, current: 1 }) );
  };

  const onEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setOpen(true);
  }

  const onCancelEdit = () => {
    setOpen(false);
    setEditingCustomer(undefined);
  }

  const onSave = (customer: Customer | undefined) => {
    setEditingCustomer(undefined);
    setOpen(false);
    customers.mutate();
  }

  const createCustomer = () => {
    setEditingCustomer(undefined);
    setOpen(true);
  }

  const onExport = async () => {
    const url = configurator.api.getCustomerExportUrl(filter, filterDealers, filterTags);
    try {
      await configurator.api.downloadCsv( url );
    }
    catch(e:any) {
      message.error( e.message );
    }
  };

  const isUploading = UPLOADING_STATUS.UPLOADING === uploadStatus;
  const importUrl = configurator.api.getCustomerImportUrl();

  const onUploadChange = ({file}) => {
    setUploadStatus( file.status );
    if (file.status === UPLOADING_STATUS.ERROR) {
      message.error( "Failed to import customers" );
    }
    else if (file.status === UPLOADING_STATUS.DONE) {
      message.success("Successfully import customers");
      customers.mutate();
    }
  };

  return (<div className="site-layout-background" key="customer-list">
    <Title level={2}>Customers</Title>

    <Row gutter={12}>
      <Col span={canViewTags ? 6 : 9}>
      <Input
        allowClear
        onChange={(value) => debounceSetFilter(value.target.value)}
        addonBefore="Filter"
        style={{marginBottom: "1rem"}}
      />
      </Col>
      <Col span={canViewTags ? 6 : 9}>
        <DealerMultipleSelector
          style={{width: "100%"}}
          onChange={onFilterDealers}
          placeholder="Please select dealer..."
        />
      </Col>
      {canViewTags &&
      <Col span={6}>
        <CustomerTagSelector
          mode="multiple"
          style={{width: "100%"}}
          onChange={onFilterTags}
          placeholder="Please select tags..."
        />
      </Col>}
      <Col>
        <Button type="primary" onClick={() => createCustomer()}>New</Button>
      </Col>
      <Col>
        <Button
          onClick={onExport}
          type="primary"
          icon={<DownloadOutlined />}
        >
          Export
        </Button>
      </Col>

      <Col>
        <Upload
          name="file"
          action={importUrl }
          withCredentials={true}
          onChange={onUploadChange}
          showUploadList={false}
          accept=".csv"
        >
          <Spin spinning={isUploading} indicator={<SyncOutlined spin style={{fontSize: "20px", color: "darkgrey"}}/>}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
            >
              Import
            </Button>
          </Spin>
        </Upload>
      </Col>
    </Row>
    <Table
      bordered
      rowKey="id"
      loading={customers.isLoading}
      columns={columns}
      dataSource={customers.data?.content}
      onChange={handleTableChange}
      pagination={{...pagination, total: customers.data?.totalElements}}
    />

    <ModalWizard
      showSteps={false}
      open={open}
      onCancel={() => {setOpen(false)}}
      steps={[
        useEditCustomerStep({
          value: editingCustomer,
          onChange: onSave,
          onCancel: onCancelEdit,
        })
      ]}
    />

  </div>);
}

export default CustomerList;
